import { useCallback, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { PackageDetail, PickupCheck, PickupLookup } from '@azko/shared';
import { PACKAGE_CODE_EXAMPLE, parsePackageCode } from '@azko/shared';
import { ApiRequestError, api } from '../lib/api.js';
import { decodeScan, extractPickupToken } from '../lib/scanner/carriers.js';
import { CameraScanner } from '../components/CameraScanner.js';
import { SignaturePad } from '../components/SignaturePad.js';
import { Alert, Button, Card, Field, PageHeader, StatusBadge, TextInput } from '../components/ui.js';
import { IconCheck } from '../components/icons.js';

/**
 * Retirada por QR, em dois passos.
 *
 * O passo de conferencia existe porque quem confirma e o porteiro, nao a
 * camera: ele ve nome, unidade e local antes de entregar. Confirmar direto na
 * leitura tornaria impossivel abortar uma entrega errada.
 *
 * A camera pausa assim que uma encomenda e encontrada. Alem de economizar
 * bateria, isso evita que o QR ainda na frente da lente dispare uma segunda
 * leitura por cima da tela de conferencia que o porteiro esta lendo.
 *
 * Ha dois caminhos ate a mesma encomenda, e eles NAO pesam igual. O QR e posse
 * — quem o mostra recebeu o link no WhatsApp do morador. O codigo curto e so
 * um localizador: quatro caracteres que o morador dita no interfone e que
 * qualquer um pode ter ouvido. Por isso a entrega pelo codigo exige nome e
 * assinatura de quem levou. Quem decide isso e a API (`requiresIdentification`
 * na resposta), nao esta tela — aqui a regra so vira campo obrigatorio.
 */
export function ScanPage() {
  const queryClient = useQueryClient();
  /** Como esta encomenda foi achada — reenviado na confirmacao. */
  const [lookup, setLookup] = useState<PickupLookup | null>(null);
  const [manualInput, setManualInput] = useState('');
  const [found, setFound] = useState<PickupCheck | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [confirmed, setConfirmed] = useState(false);
  const [signature, setSignature] = useState<string | null>(null);
  const [pickedUpByName, setPickedUpByName] = useState('');
  const [pickedUpByDocument, setPickedUpByDocument] = useState('');
  /**
   * Quais caixas o morador esta levando AGORA.
   *
   * Comeca com todas marcadas porque levar o carrinho inteiro e o caso normal —
   * o porteiro so mexe aqui quando o morador deixa alguma para depois. O
   * caminho de menos toques continua sendo o de sempre: ler o QR e confirmar.
   */
  const [selectedBoxes, setSelectedBoxes] = useState<string[]>([]);

  const validate = useMutation({
    mutationFn: (value: PickupLookup) =>
      api.post<PickupCheck>('/packages/pickups/validate', value),
    onSuccess: (result) => {
      setFound(result);
      setMessage(null);
      setConfirmed(false);
      // Encomenda nova, assinatura e identificacao novas: sem isto o traco e o
      // nome da entrega anterior seriam enviados como comprovante desta.
      setSignature(null);
      setPickedUpByName('');
      setPickedUpByDocument('');
      setSelectedBoxes(result.boxes.map((box) => box.id));
      // Vibra no celular: o porteiro esta olhando o pacote, nao a tela.
      navigator.vibrate?.(60);
    },
    onError: (error) => {
      setFound(null);
      setMessage(error instanceof ApiRequestError ? error.message : 'Falha ao localizar a encomenda.');
    },
  });

  const confirm = useMutation({
    mutationFn: () =>
      api.post<PackageDetail>('/packages/pickups/confirm', {
        ...lookup,
        ...(pickedUpByName.trim() ? { pickedUpByName: pickedUpByName.trim() } : {}),
        ...(pickedUpByDocument.trim() ? { pickedUpByDocument: pickedUpByDocument.trim() } : {}),
        // Vai junto da confirmacao, nao num upload a parte: ou a entrega grava
        // com o comprovante, ou nao grava.
        ...(signature ? { signature } : {}),
        // Em lote vai SEMPRE, mesmo com tudo marcado — e nao so quando a
        // retirada e parcial. O que a API recebe passa a ser exatamente o que o
        // porteiro viu na tela, entao uma caixa que outro terminal entregou no
        // meio do caminho vira um erro claro em vez de uma entrega silenciosa
        // de duas onde a tela dizia tres.
        ...(found && found.boxes.length > 0 ? { boxIds: selectedBoxes } : {}),
      }),
    onSuccess: (result) => {
      setFound((current) => (current ? { ...current, package: result } : current));
      setConfirmed(true);
      setMessage(null);
      void queryClient.invalidateQueries({ queryKey: ['packages'] });
    },
    onError: (error) => {
      setMessage(error instanceof ApiRequestError ? error.message : 'Falha ao confirmar a entrega.');
    },
  });

  const submitLookup = useCallback(
    (value: PickupLookup) => {
      setLookup(value);
      validate.mutate(value);
    },
    [validate],
  );

  const handleDecode = useCallback(
    (raw: string) => {
      const decoded = decodeScan(raw);

      // Ler a etiqueta da transportadora aqui e um engano comum de quem acabou
      // de registrar a entrada. Dizer O QUE foi lido evita a conclusao errada
      // de que a camera nao esta funcionando.
      if (decoded.kind !== 'pickup' || !decoded.pickupToken) {
        setMessage('Isso nao e um QR de retirada. Aponte para o codigo que o morador recebeu.');
        return;
      }

      submitLookup({ token: decoded.pickupToken });
    },
    [submitLookup],
  );

  function reset() {
    setLookup(null);
    setManualInput('');
    setFound(null);
    setMessage(null);
    setConfirmed(false);
    setSignature(null);
    setPickedUpByName('');
    setPickedUpByDocument('');
    setSelectedBoxes([]);
  }

  function toggleBox(boxId: string) {
    setSelectedBoxes((current) =>
      current.includes(boxId)
        ? current.filter((id) => id !== boxId)
        : [...current, boxId],
    );
  }

  // Camera parada enquanto ha uma encomenda na tela para conferir.
  const scanning = found === null;

  const pkg = found?.package ?? null;
  const needsIdentification = found?.requiresIdentification ?? false;
  const manualLookup = parseManualEntry(manualInput);

  const boxes = found?.boxes ?? [];

  // Espelha o que o service exige. A API recusa de novo se algo passar daqui —
  // o bloqueio na tela existe para o porteiro nao descobrir no 400 que faltava
  // a assinatura depois de a pessoa ja ter ido embora.
  //
  // Zero caixas marcadas trava o botao pelo mesmo motivo, e este e o caso mais
  // perigoso da tela: a API trata lista vazia como erro, nunca como "leve
  // tudo", e o botao desligado diz isso antes de o porteiro tentar.
  const readyToConfirm =
    (!needsIdentification || (pickedUpByName.trim().length >= 3 && signature !== null)) &&
    (boxes.length === 0 || selectedBoxes.length > 0);

  return (
    <>
      <PageHeader title="Ler QR de retirada" />

      <Card className="mx-auto max-w-2xl p-5 sm:p-6">
        <div className="flex items-start justify-between gap-4">
          <div>
            <p className="eyebrow">Validacao</p>
            <h2 className="mt-2 text-2xl font-bold tracking-tight">QR de retirada</h2>
          </div>
          {validate.isPending && <StatusBadge tone="accent">Lendo</StatusBadge>}
        </div>

        {scanning || !pkg ? (
          <CameraScanner
            active
            onDecode={handleDecode}
            hint="Mantenha o QR dentro da mira — a leitura e automatica."
            className="mt-6"
          />
        ) : (
          <div className="mt-6 rounded-card border border-accent/40 bg-accent/5 p-5">
            <p className="flex items-center gap-2 font-bold">
              <IconCheck className="size-5 text-accent" />
              {confirmed
                ? pkg.boxesAwaiting > 0
                  ? 'Entrega parcial confirmada'
                  : 'Entrega confirmada'
                : 'Encomenda encontrada'}{' '}
              — #{pkg.code}
            </p>
            <p className="mt-2 text-sm text-muted">
              {pkg.residentName ?? 'Sem destinatario'} • Unidade {pkg.unitLabel}
              {pkg.storageLocation && ` • ${pkg.storageLocation}`}
              {pkg.boxCount > 1 && ` • ${pkg.boxesAwaiting} de ${pkg.boxCount} caixas na portaria`}
            </p>
            {pkg.carrier && (
              <p className="mt-1 text-sm text-faint">
                {pkg.carrier}
                {pkg.trackingCode && ` • ${pkg.trackingCode}`}
              </p>
            )}

            {/* Some depois de confirmado: o comprovante ja foi gravado e um
                quadro de assinatura em branco ali sugeriria que da para
                assinar de novo. */}
            {!confirmed && (
              <div className="mt-5 space-y-5 border-t border-line-soft pt-5">
                {/* A lista de caixas so aparece em lote, e vem ANTES da
                    assinatura: o que se assina e a entrega destas caixas, e o
                    porteiro precisa ter conferido quais antes de pedir o traco.
                    So as que ainda aguardam chegam aqui — caixa ja entregue na
                    lista seria um convite a entregar de novo. */}
                {boxes.length > 0 && (
                  <div>
                    <p className="text-sm font-bold">
                      {boxes.length} caixas aguardando — marque o que esta saindo
                    </p>
                    <p className="mt-1 text-sm text-muted">
                      Levando tudo? Deixe como esta. O que ficar desmarcado continua na
                      portaria e o QR do morador segue valendo.
                    </p>

                    <ul className="mt-3 space-y-2">
                      {boxes.map((box) => (
                        <li key={box.id}>
                          <label className="flex cursor-pointer items-center gap-3 rounded-xl border border-line bg-surface-2 p-3">
                            <input
                              type="checkbox"
                              checked={selectedBoxes.includes(box.id)}
                              onChange={() => toggleBox(box.id)}
                              className="size-5 shrink-0 accent-accent"
                            />
                            <span className="min-w-0">
                              <span className="block text-sm font-bold">
                                Caixa {box.seq} — #{box.code}
                              </span>
                              <span className="block text-sm text-muted">
                                {box.storageLocation || 'Sem local definido'}
                                {box.trackingCode && ` • ${box.trackingCode}`}
                              </span>
                            </span>
                          </label>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {needsIdentification && (
                  <>
                    <Alert>
                      Encontrada pelo codigo, nao pelo QR. O codigo nao prova que quem esta no
                      balcao e o morador — registre quem esta levando e peca a assinatura.
                    </Alert>

                    <Field label="Quem esta retirando">
                      <TextInput
                        value={pickedUpByName}
                        onChange={(event) => setPickedUpByName(event.target.value)}
                        placeholder="Nome completo"
                        autoComplete="off"
                      />
                    </Field>

                    <Field label="Documento (opcional)">
                      <TextInput
                        value={pickedUpByDocument}
                        onChange={(event) => setPickedUpByDocument(event.target.value)}
                        placeholder="RG ou CPF"
                        autoComplete="off"
                      />
                    </Field>
                  </>
                )}

                <SignaturePad onChange={setSignature} disabled={confirm.isPending} />
              </div>
            )}

            <div className="mt-5 flex flex-wrap gap-3">
              {!confirmed && (
                <Button
                  onClick={() => confirm.mutate()}
                  loading={confirm.isPending}
                  disabled={!readyToConfirm}
                >
                  Confirmar entrega
                </Button>
              )}
              <Button variant="ghost" onClick={reset}>
                {confirmed ? 'Proxima encomenda' : 'Cancelar'}
              </Button>
            </div>

            {!confirmed && !readyToConfirm && (
              <p className="mt-3 text-sm text-faint">
                Falta{' '}
                {boxes.length > 0 && selectedBoxes.length === 0
                  ? 'marcar ao menos uma caixa'
                  : pickedUpByName.trim().length < 3
                    ? 'o nome de quem retira'
                    : 'a assinatura'}
                .
              </p>
            )}
          </div>
        )}

        {message && <div className="mt-4"><Alert>{message}</Alert></div>}

        {/* Digitacao continua existindo: QR rasgado, tela quebrada do morador,
            o link chegando por mensagem para a portaria — ou o morador que
            desceu sem o celular e so sabe o codigo. */}
        {scanning && (
          <details className="mt-5">
            <summary className="text-sm font-semibold text-muted">
              Nao consegue ler? Digite o codigo
            </summary>

            <div className="mt-4 space-y-4">
              <Field label="Codigo da encomenda ou link de retirada">
                <TextInput
                  value={manualInput}
                  onChange={(event) => setManualInput(event.target.value)}
                  placeholder={`${PACKAGE_CODE_EXAMPLE}, ou cole o link do morador`}
                  autoComplete="off"
                />
              </Field>

              <Button
                variant="ghost"
                className="w-full"
                loading={validate.isPending}
                disabled={manualLookup === null}
                onClick={() => {
                  if (manualLookup) submitLookup(manualLookup);
                }}
              >
                Buscar encomenda
              </Button>
            </div>
          </details>
        )}
      </Card>
    </>
  );
}

/**
 * O que o porteiro digitou: link/token de retirada, ou o codigo curto.
 *
 * O token e testado primeiro porque e o unico que prova posse — e porque um
 * link colado tambem casaria com "texto qualquer" se a ordem fosse a inversa.
 */
function parseManualEntry(value: string): PickupLookup | null {
  const token = extractPickupToken(value);
  if (token) return { token };

  const code = parsePackageCode(value);
  return code ? { code } : null;
}
